import * as React from 'react'
import { useQuery } from '@apollo/react-hooks'
import { isAdmin, getCurrentUser } from 'web-fns/dist/auth'
import { Helmet } from 'react-helmet'
import { Redirect } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { createGlobalStyle } from 'styled-components'

import { client as apolloClient } from '../ApolloProvider/ApolloProvider'
import { GET_BRAND_PROFILE } from '../../queries/GetBrandProfileV2'
import { getDashboardId } from '../../helpers/getDashboardId'

import EnvironmentContext from '../EnvironmentContext'

import SubHeader from './components/SubHeader/SubHeader'
import DashboardHeader from './components/DashboardHeader/DashboardHeader'
import { Container, Content, DashboardContent } from './BrandDashboard.style'
import Navigation, { Menu } from './components/Navigation/Navigation'

export interface NewBrandDashboardProps {
  children?: React.ReactNode | any
  subHeaderChildren?: React.ReactNode | any
  activeButton?: number
  brandId?: string
  subHeaderTitle?: string
  isSubHeaderHidden?: boolean | false
  searchToken?: string
}

const NewBrandDashboard: React.FC<NewBrandDashboardProps> = ({
  children,
  subHeaderChildren,
  activeButton,
  brandId,
  subHeaderTitle,
  isSubHeaderHidden,
  searchToken,
}) => {
  const [currentBrandId, setCurrentBrandId] = React.useState<string | null>(null)
  const { DELIVERY_URL, CONCIERGE_URL, PRODUCTIZE_URL, CONTENT_URL } =
    React.useContext(EnvironmentContext) || ''
  const [isNavCollapsed, setIsNavCollapsed] = React.useState<boolean>(false)
  const {
    data: brandProfileData,
    refetch: refetchbrandProfileData,
    loading: profileLoading,
  } = useQuery(GET_BRAND_PROFILE, {
    variables: { brandUuid: getDashboardId() },
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    client: apolloClient,
    skip: activeButton === 0,
  })

  const { getBrandProfile } = brandProfileData || ''

  const currentUser = getCurrentUser() as any
  const { name: userName = '', avatar: userAvatar, id: userId } = currentUser || {}

  const refetchCallback = () => refetchbrandProfileData({ brandUuid: getDashboardId() || '' })
  const isSubBrandView = localStorage.getItem('isSubBrand')

  const RedirectPage = () => {
    if (getDashboardId() === 'team') {
      return <Redirect push={true} to={`/brand-dashboard/team/${getDashboardId()}`} />
    }
    return
  }

  const menuItems: Menu[] = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      slug: `/brand-dashboard/${brandId}`,
      isSelected: activeButton === 0,
    },
    {
      id: 'new-order',
      name: 'New Order',
      slug: `/brand-dashboard/orders/${brandId}`,
      isSelected: activeButton === 2 || activeButton === 1,
    },
    {
      id: 'orders',
      name: 'Orders',
      slug: `/brand-dashboard/${brandId}`,
    },
    {
      id: 'wallets',
      name: 'Wallets  ',
      slug: `/brand-dashboard/wallets/${brandId}`,
      isSelected: activeButton === 3,
    },
    {
      id: 'delivery',
      name: 'Delivery',
      slug: `${DELIVERY_URL}/brands/${brandId}`,
      isExternal: true,
    },
    {
      id: 'content',
      name: 'Content',
      slug: `${CONTENT_URL}/brands/${brandId}`,
      isExternal: true,
    },
    {
      id: 'account',
      name: 'Account',
      slug: `/brand-dashboard/settings/${brandId}`,
      isSelected: activeButton === 4 || activeButton === 5 || activeButton === 7,
    },
  ]
  const getConciergeSlug = (path: string) => {
    return `${CONCIERGE_URL}${brandId ? `/brands/${brandId}` : ''}${path}`
  }
  const adminMenuItems: Menu[] = [
    {
      id: 'dashboard',
      name: 'Dashboard',
      slug: getConciergeSlug('/orders'),
      isExternal: true,
    },
    {
      id: 'productize',
      name: 'Productize',
      slug: `${PRODUCTIZE_URL}`,
      isExternal: true,
    },
    {
      id: 'orders',
      name: 'Orders',
      slug: getConciergeSlug('/orders'),
      isExternal: true,
    },
    {
      id: 'wallets',
      name: 'Wallets',
      slug: getConciergeSlug('/wallets'),
      isExternal: true,
    },
    {
      id: 'matching',
      name: 'Matching',
      slug: getConciergeSlug('/matching'),
      isExternal: true,
    },
    {
      id: 'delivery',
      name: 'Delivery',
      slug: getConciergeSlug('/delivery'),
      isExternal: true,
    },
    {
      id: 'content',
      name: 'Content',
      slug: getConciergeSlug('/content'),
      isExternal: true,
    },
    {
      id: 'our-brand-default',
      name: 'Brands',
      slug: getConciergeSlug('/brands'),
      isExternal: true,
    },
    {
      id: 'creators',
      name: 'Creators',
      slug: getConciergeSlug('/creators'),
      isExternal: true,
    },
  ]
  const flags = useFlags()
  const enable2024NavigationBlack = flags['2024-navigation-black']

  const GlobalHeaderStyles = createGlobalStyle`
    /* stylelint-disable */
    #navigation-bar {
      ${enable2024NavigationBlack &&
        `
        left: 210px;
        max-width: calc(100% - 210px);
      `}
      @media (max-width: 64rem) {
        left: 0;
        max-width: 100%;
      }
    }
    body.dashboard .main-content{
      ${enable2024NavigationBlack && `padding-top: 1.5rem;`}
    }
    /* stylelint-enable */
  `
  return (
    <Container isManager={isAdmin()} newSidebarActive={enable2024NavigationBlack}>
      {RedirectPage()}
      <Content
        isSubBrandView={isSubBrandView}
        newSidebarActive={enable2024NavigationBlack}
        isNavCollapsed={isNavCollapsed}
      >
        <Helmet>
          <meta
            name="apple-itunes-app"
            content="app-id=729925714, app-argument=https://apps.apple.com/us/app/90-seconds/id729925714?itsct=apps_box_link&itscg=30200"
          />
        </Helmet>
        <GlobalHeaderStyles />
        {enable2024NavigationBlack && (
          <SubHeader
            activeMenu={activeButton}
            urlBrandId={getDashboardId()}
            title={subHeaderTitle || ''}
            subHeaderChildren={subHeaderChildren}
            isSubHeaderHidden={isSubHeaderHidden}
            isNavCollapsed={isNavCollapsed}
          />
        )}
        {enable2024NavigationBlack ? (
          <Navigation
            activeButton={activeButton}
            menuProps={menuItems}
            adminMenuProps={adminMenuItems}
            searchToken={searchToken}
            callbackCurrentBrandId={setCurrentBrandId}
            userForAvatar={{
              name: userName,
              avatar: userAvatar,
              id: userId,
            }}
            callbackTrigger={e => setIsNavCollapsed(e)}
          />
        ) : (
          <DashboardHeader
            activeButton={activeButton}
            brandProfileData={getBrandProfile && getBrandProfile}
            brandId={brandId}
          />
        )}
        <DashboardContent className="main-content" newSidebarActive={enable2024NavigationBlack}>
          {children &&
            React.cloneElement(children, {
              brandOrderUuid: currentBrandId,
              brandProfile: brandProfileData && getBrandProfile,
              searchToken,
              refetchBrandProfile: refetchCallback,
              profileLoading,
            })}
        </DashboardContent>
      </Content>
    </Container>
  )
}

export default NewBrandDashboard



// WEBPACK FOOTER //
// ./src/components/BrandDashboard/BrandDashboard.tsx